import { createModel } from "@rematch/core";
import { TokenError } from "checkout/ts/errors/TokenError";
import { LOCALE_LABELS } from "checkout/ts/locale/labels";
import { getMessage } from "checkout/ts/utils/intl";
import { ResourceType } from "common/Messages";
import { uniqBy } from "lodash";
import { Metadata, TransactionTokenItem, TransactionTokenType, TransactionTokenUpdateParams } from "univapay-node";

import { PatchedTokenCardData, ThreeDsStatus } from "../sdk/resources/TransactionTokens";
import { sdk } from "../sdk/SDK";
import { Dispatch, StateShape } from "../store";

export type TokensMap = {
    [uuid: string]: TransactionTokenItem;
};

export type PatchedTransactionTokenUpdateParams = Omit<TransactionTokenUpdateParams, "data"> & {
    data?: {
        cvv?: string;
        line1?: string;
        line2?: string;
        state?: string;
        city?: string;
        country?: string;
        zip?: string;
        phoneNumber?: {
            countryCode?: string;
            localNumber?: string;
        };
    };

    /**
     * Metadata or stringified JSON object
     */
    metadata?: string | Metadata;
};

export const getDefaultSelectedTokenId = (tokens: TransactionTokenItem[]): string =>
    tokens?.length > 0
        ? tokens.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()).reverse()[0].id
        : "";

type ModelStateShape = {
    tokens?: TokensMap;
    error: any;
    cvvRequired: boolean;
};

const initialState: ModelStateShape = {
    tokens: null,
    error: null,
    cvvRequired: false,
};

const model = {
    state: initialState,

    reducers: {
        setTokens: (state: ModelStateShape, { tokenMap }: { tokenMap: TokensMap }) => ({ ...state, tokens: tokenMap }),
        updateToken: (state: ModelStateShape, { token }: { token: TransactionTokenItem }) => ({
            ...state,
            tokens: { ...state.tokens, [token.id]: token },
        }),
        setError: (state: ModelStateShape, { error }: { error: Error }) => ({ ...state, error }),
        setCvvRequired: (state: ModelStateShape, { cvvRequired }: { cvvRequired: boolean }) => ({
            ...state,
            cvvRequired,
        }),

        clearTokens: (state: ModelStateShape) => ({ ...state, tokens: null }),
        clearError: (state: ModelStateShape) => ({ ...state, error: null }),
    },

    effects: (dispatch: Dispatch) => ({
        list: async (_?, state?: StateShape): Promise<TokensMap> => {
            const { tokens: self } = dispatch;
            const { univapayCustomerId } = state.application.params.params;

            self.clearTokens();
            self.clearError();

            try {
                if (univapayCustomerId) {
                    const tokens = await sdk.transactionTokens.list({
                        customerId: univapayCustomerId,
                        type: TransactionTokenType.RECURRING,
                        mode: state.configuration.data.mode,
                        limit: 100,
                    });

                    const hasValidThreeDsStatus = (token) => {
                        const { threeDs } = (token?.data as PatchedTokenCardData) || {};

                        return !threeDs || !threeDs?.enabled || threeDs?.status === ThreeDsStatus.SUCCESSFUL;
                    };

                    // Get full tokens
                    const fullTokens: TransactionTokenItem[] = [];
                    const listItems = uniqBy((tokens?.items || []).filter(hasValidThreeDsStatus), "id");
                    for (const token of listItems) {
                        const fullToken = await sdk.transactionTokens.get(token.storeId, token.id);

                        if (hasValidThreeDsStatus(fullToken)) {
                            fullTokens.push(fullToken);
                        }
                    }

                    const tokenMap = fullTokens.reduce((acc, token) => {
                        acc[token.id] = token;
                        return acc;
                    }, {});

                    self.setTokens({ tokenMap });

                    return tokenMap;
                } else {
                    self.setTokens({ tokenMap: {} });

                    return {};
                }
            } catch (error) {
                self.setError({ error });
            }
        },

        getRecurring: async ({ storeId, id }: { storeId: string; id: string }): Promise<TransactionTokenItem> => {
            const { tokens: self, application } = dispatch;

            try {
                const token = await sdk.transactionTokens.get(storeId, id);

                if (token?.type !== TransactionTokenType.RECURRING) {
                    throw new Error("Invalid token type");
                }

                self.updateToken({ token });

                return token;
            } catch (error) {
                application.setError({ error });
            }
        },

        enableThreeDs: async (
            { storeId, id }: { storeId: string; id: string },
            state: StateShape
        ): Promise<TransactionTokenItem> => {
            let token: TransactionTokenItem;
            try {
                let token = await sdk.patchedTransactionTokens.enableThreeDs(storeId, id);
                await sdk.patchedTransactionTokens.pollPendingThreeDsToken(storeId, id);
                token = await sdk.patchedTransactionTokens.pollThreeDs(
                    storeId,
                    id,
                    state.intl.locale,
                    state.application.connector
                );

                if (token.data?.threeDs?.status !== ThreeDsStatus.SUCCESSFUL) {
                    throw new TokenError(
                        token.id,
                        undefined,
                        token.storeId,
                        getMessage(LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED),
                        LOCALE_LABELS.ERRORS_ALERTS_THREE_DS_FAILED
                    );
                }

                state.application.connector.emitter.emit("checkout:three-ds-authorization-success", {
                    resourceType: ResourceType.TRANSACTION_TOKEN,
                    response: token,
                    tokenId: token?.id,
                });

                return token;
            } catch (error) {
                state.application.connector.emitter.emit("checkout:three-ds-authorization-failure", {
                    error,
                    resourceType: ResourceType.TRANSACTION_TOKEN,
                    response: token,
                    tokenId: id,
                });

                throw error;
            }
        },
    }),
};

export const tokens = createModel()(model);
